import { Navigate } from '@tanstack/react-router'
import { type FormEvent, useState } from 'react'
import { StepUpBanner } from '../components/step-up-banner'
import { Alert, Button, Field, Input } from '../components/ui'
import { stepUpPath } from '../lib/api'
import { authClient } from '../lib/auth-client'
import { useMe } from '../lib/session'

export interface SignInSearch {
  error?: string
  /** Set when the API answered `403 step_up_required`: the user is signed in but must re-authenticate. */
  step_up?: 1
  /** Same-origin path to return to after re-authentication. */
  redirect?: string
}

function absolute(path: string): string {
  return new URL(path, window.location.origin).toString()
}

/**
 * Magic-link (dealers, borrowers) and Google Workspace (Tex staff) sign-in.
 * `redirectTo` is where Better Auth lands the browser after the link/OAuth callback.
 */
export function SignInForm({
  redirectTo = '/',
  invitationEmail,
  stepUp = false,
}: {
  redirectTo?: string
  invitationEmail?: string
  /** Re-authentication of a signed-in user: keep the step-up context on the error return path too. */
  stepUp?: boolean
}) {
  const [email, setEmail] = useState(invitationEmail ?? '')
  const errorUrl = (error: string) =>
    absolute(stepUp ? `${stepUpPath(redirectTo)}&error=${error}` : `/sign-in?error=${error}`)
  const [sent, setSent] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  async function sendLink(e: FormEvent) {
    e.preventDefault()
    setBusy(true)
    setError(null)
    const res = await authClient.signIn.magicLink({
      email: email.trim().toLowerCase(),
      callbackURL: absolute(redirectTo),
      errorCallbackURL: errorUrl('link'),
    })
    setBusy(false)
    if (res.error) setError(res.error.message ?? res.error.statusText)
    else setSent(email.trim().toLowerCase())
  }

  async function google() {
    setBusy(true)
    setError(null)
    const res = await authClient.signIn.social({
      provider: 'google',
      callbackURL: absolute(redirectTo),
      errorCallbackURL: errorUrl('google'),
    })
    if (res.error) {
      setBusy(false)
      setError(res.error.message ?? res.error.statusText)
    }
  }

  if (sent) {
    return (
      <Alert tone="success">
        If <strong>{sent}</strong> is on file, a sign-in link is on its way. It expires in 15 minutes.
      </Alert>
    )
  }

  return (
    <div className="space-y-4">
      <form onSubmit={sendLink} className="space-y-3">
        <Field label="Work email" hint={invitationEmail ? 'Sign in with the address the invitation was sent to.' : undefined}>
          <Input
            type="email"
            required
            autoComplete="email"
            value={email}
            readOnly={invitationEmail !== undefined}
            onChange={(e) => setEmail(e.target.value)}
          />
        </Field>
        <Button type="submit" disabled={busy} className="w-full py-2.5">
          Email me a sign-in link
        </Button>
      </form>
      <div className="flex items-center gap-3 font-sans text-xs uppercase tracking-wider text-neutral-400">
        <hr className="flex-1 border-neutral-100" />
        Tex staff
        <hr className="flex-1 border-neutral-100" />
      </div>
      <Button variant="secondary" disabled={busy} onClick={google} className="w-full py-2.5">
        Continue with Google Workspace
      </Button>
      {error && <Alert tone="error">{error}</Alert>}
    </div>
  )
}

export function SignInPage({ search }: { search: SignInSearch }) {
  const me = useMe()
  const redirectTo = search.redirect ?? '/'
  if (me.data && !search.step_up) return <Navigate to="/" />

  return (
    <section className="tex-grid-texture flex min-h-[70vh] items-center justify-center rounded-2xl px-4 py-12">
      <div className="w-full max-w-sm space-y-6 rounded-xl border border-[var(--border-subtle)] bg-white p-8 shadow-md">
        <div className="flex items-center gap-2">
          <img src="/tex-logo.png" alt="Tex" width={32} height={32} className="h-8 w-8 translate-y-1.5" />
          <span
            className="translate-y-1.5 font-display text-2xl font-black uppercase text-tex-red"
            style={{ letterSpacing: '-0.04em' }}
          >
            Tex Finance
          </span>
        </div>
        <div>
          <h1 className="font-display text-2xl font-bold text-neutral-900">{search.step_up ? 'Sign in again' : 'Sign in'}</h1>
          <p className="mt-1 text-sm text-neutral-500">
            Dealers and borrowers sign in with an emailed link. Tex staff use Google Workspace.
          </p>
        </div>
        {search.step_up && <StepUpBanner returnTo={redirectTo} />}
        {search.error && <Alert tone="error">That sign-in link is invalid or expired. Request a new one.</Alert>}
        <SignInForm redirectTo={redirectTo} stepUp={search.step_up === 1} />
      </div>
    </section>
  )
}
